import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import { ForecastTemplateColumns } from 'src/models/ForecastModels';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';
import {
  BUSINESS_GROUP_CODES,
  DMM_BUSINESS_SEGMENT_ORDER,
  ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS,
  ECHO_PRODUCT_LINE_CODE
} from 'src/constants/business-group-constants';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';

/**
 * Service for validating mandatory fields in forecast templates
 */
export class MandatoryFieldsValidationService extends ValidationService {
  /**
   * Validates that all mandatory fields are present in each object of the file data.
   * @param forecastTemplateRelevantRowData The array of data objects from the file.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param isDMM Whether the business group is DMM
   * @returns Promise with validation status
   */
  public async validateMandatoryFields(
    forecastTemplateRelevantRowData: any[],
    forecastTemplateColumns: ForecastTemplateColumns,
    isDMM: boolean
  ): Promise<ValidationStatusEntity> {
    logger.info(`Validating Mandatory Fields for ${forecastTemplateRelevantRowData.length} records`);

    const validationErrorDetails: ValidationErrorDetail[] = [];
    const mandatoryFields = forecastTemplateColumns.mandatoryFields;

    // For error messages, filter out description fields
    const corpSegments = forecastTemplateColumns.corpSegmentMandatoryFields?.filter((corpSegment) => !corpSegment.endsWith(' Description'));
    const businessSegments = forecastTemplateColumns.businessSegmentMandatoryFields?.filter(
      (businessSegment) => !businessSegment.endsWith(' Description')
    );

    const errorMessageMandatoryFields = corpSegments.concat(businessSegments);
    const defaultValidationMessage = ForecastValidationMessages.MANDATORY_FIELDS_VALIDATION_DEFAULT_MESSAGE(errorMessageMandatoryFields);

    forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
      // Determine if this is an ECHO product line
      const isEchoProductLine = row[CorpSegmentNames.PRODUCT_LINE] === ECHO_PRODUCT_LINE_CODE;

      // Get the appropriate mandatory fields based on product line
      let fieldsToValidate = [...mandatoryFields];

      if (isEchoProductLine && isDMM) {
        // For ECHO, add fields that are optional for others but mandatory for ECHO
        const additionalMandatoryFields = DMM_BUSINESS_SEGMENT_ORDER.filter((field) => !ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS.includes(field));
        fieldsToValidate = [...new Set([...fieldsToValidate, ...additionalMandatoryFields])];
      }

      // Check for missing fields
      const missingFields = fieldsToValidate.filter(
        (field) => !row.hasOwnProperty(field) || row[field] === null || row[field] === undefined || row[field] === ''
      );

      // Add validation errors for missing fields
      if (missingFields.length > 0) {
        missingFields.forEach((field) => {
          validationErrorDetails.push({
            rowIndex: rowIndex + 1,
            message: `Required field - ${field} ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
          });
        });
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Mandatory Fields', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(ForecastValidationMessages.MANDATORY_FIELDS_VALIDATION_FAILED, defaultValidationMessage, validationErrorDetails);
    }

    return this.createSuccessStatus(ForecastValidationMessages.MANDATORY_FIELDS_VALIDATION_SUCCESS, defaultValidationMessage);
  }
}

// Export singleton instance
export const mandatoryFieldsValidationService = new MandatoryFieldsValidationService();
